import React from "react";
import { Form, Input, Select, DatePicker, InputNumber, Checkbox, Divider, Row, Col, Typography, Space } from "antd";
import { TagOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { detailFieldsMapping, historyTypesOptions } from "../../../../../types/healthcare/MedicalHistoryTypes";
import { MedicalHistory } from "../../../../../types/healthcare/MedicalHistoryTypes";
import { loadUserPreferences } from "../../../../../utils/userPrefs/UserPrefs";
import dayjs from "dayjs";
import { SectionCard } from "../../../../../components/shared";

const { TextArea } = Input;
const { Title, Text } = Typography;

interface CreateMedicalHistoryModalProps {
	isEditing: boolean;
	currentRecord: Partial<MedicalHistory>;
	setCurrentRecord: React.Dispatch<React.SetStateAction<Partial<MedicalHistory | null>>>;
}

const CreateMedicalHistoryModal: React.FC<CreateMedicalHistoryModalProps> = ({ isEditing, currentRecord, setCurrentRecord }) => {
	// Render form fields based on the selected history type
	const renderDetailFields = () => {
		if (!currentRecord?.history_type) return null;

		const fields = detailFieldsMapping[currentRecord.history_type as keyof typeof detailFieldsMapping];

		if (!fields)
			return (
				<Text type="secondary" style={{ fontStyle: "italic", display: "block", textAlign: "center", padding: 16 }}>
					No fields defined for this history type.
				</Text>
			);

		return (
			<Row gutter={[16, 16]}>
				{fields.map((field) => (
					<Col xs={24} md={12} key={field.name}>
						<Form.Item label={field.label}>
							{field.type === "text" && <Input value={currentRecord?.details && currentRecord.details[field.name] ? currentRecord.details[field.name] : ""} onChange={(e) => handleDetailChange(field.name, e.target.value)} />}

							{field.type === "textarea" && (
								<TextArea value={currentRecord?.details && currentRecord.details[field.name] ? currentRecord.details[field.name] : ""} onChange={(e) => handleDetailChange(field.name, e.target.value)} rows={3} autoSize />
							)}

							{field.type === "date" && (
								<DatePicker
									style={{ width: "100%" }}
									value={currentRecord?.details && currentRecord.details[field.name] ? dayjs(currentRecord.details[field.name], "YYYY-MM-DD") : null}
									onChange={(date) => handleDetailChange(field.name, date ? date.format("YYYY-MM-DD") : null)}
									format={loadUserPreferences().dateFormat}
								/>
							)}

							{field.type === "number" && (
								<InputNumber style={{ width: "100%" }} value={currentRecord?.details && currentRecord.details[field.name] ? currentRecord.details[field.name] : null} onChange={(value) => handleDetailChange(field.name, value)} />
							)}

							{field.type === "boolean" && (
								<Checkbox checked={currentRecord?.details && currentRecord.details[field.name] ? currentRecord.details[field.name] : false} onChange={(e) => handleDetailChange(field.name, e.target.checked)}>
									Yes
								</Checkbox>
							)}

							{field.type === "dropdown" && "options" in field && (
								<Select
									style={{ width: "100%" }}
									value={currentRecord?.details && currentRecord.details[field.name] ? currentRecord.details[field.name] : null}
									options={field.options}
									onChange={(value) => handleDetailChange(field.name, value)}
									placeholder="Select"
									showSearch
								/>
							)}
						</Form.Item>
					</Col>
				))}
			</Row>
		);
	};

	// Handler for updating detail fields in the currentRecord
	const handleDetailChange = (fieldName: string, value: any) => {
		setCurrentRecord((prev) => ({
			...prev,
			details: {
				...(prev?.details || {}),
				[fieldName]: value,
			},
		}));
	};

	// Get history type label for display
	const getHistoryTypeLabel = () => {
		if (!currentRecord?.history_type) return "";
		const option = historyTypesOptions.find((opt) => opt.value === currentRecord.history_type);
		return option ? option.label : "";
	};

	const dateFormat = loadUserPreferences().dateFormat || "DD/MM/YYYY";

	return (
		<div>
			<Row gutter={[16, 16]}>
				<Col xs={24} md={6}>
					<SectionCard>
						<Form layout="vertical">
							<Form.Item
								label={
									<span>
										<Text strong>History Type</Text> <Text type="danger">*</Text>
									</span>
								}
							>
								<Select
									value={currentRecord?.history_type || ""}
									options={historyTypesOptions}
									onChange={(value) => {
										// When changing type, clear out the existing details
										setCurrentRecord((prev) => ({
											...prev,
											history_type: value,
											details: {},
										}));
									}}
									placeholder="Select history type"
									showSearch
									filterOption={(input, option) => (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())}
									optionRender={(option) => (
										<Space>
											<TagOutlined style={{ color: "var(--mg-primary)" }} />
											<span>{option.label}</span>
										</Space>
									)}
								/>
							</Form.Item>

							<Form.Item label={<Text strong>Description</Text>}>
								<TextArea rows={7} value={currentRecord?.description || ""} onChange={(e) => setCurrentRecord((prev) => ({ ...prev, description: e.target.value }))} placeholder="Enter additional notes or context..." autoSize />
							</Form.Item>

							<SectionCard style={{ background: "var(--mg-bg-subtle)", marginTop: 16 }}>
								<Space align="start">
									<InfoCircleOutlined style={{ color: "var(--mg-primary)", fontSize: 16 }} />
									<Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
										{currentRecord?.history_type ? `Completing details for ${getHistoryTypeLabel()}` : "Select a type to reveal form fields"}
									</Text>
								</Space>
							</SectionCard>
						</Form>
					</SectionCard>
				</Col>

				<Col xs={24} md={18}>
					{currentRecord?.history_type ? (
						<SectionCard>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
								<Space>
									<EditOutlined style={{ color: "var(--mg-primary)" }} />
									<Title level={5} style={{ margin: 0 }}>
										Details: {getHistoryTypeLabel()}
									</Title>
								</Space>
								{isEditing ? (
									<span style={{ background: "var(--mg-info-bg)", color: "var(--mg-info)", padding: "4px 12px", borderRadius: 16, fontSize: 14 }}>Editing Existing Record</span>
								) : (
									<span style={{ background: "var(--mg-success-bg)", color: "var(--mg-success)", padding: "4px 12px", borderRadius: 16, fontSize: 14 }}>Creating New Record</span>
								)}
							</div>
							<Divider style={{ marginTop: 12, marginBottom: 16 }} />
							<Form layout="vertical">{renderDetailFields()}</Form>
						</SectionCard>
					) : (
						<SectionCard style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", border: "2px dashed var(--mg-border)" }}>
							<div style={{ textAlign: "center", padding: 48 }}>
								<EditOutlined style={{ fontSize: 64, color: "var(--mg-text-disabled)", marginBottom: 16 }} />
								<Title level={4} style={{ marginTop: 16, marginBottom: 12 }}>
									Select a History Type
								</Title>
								<Text type="secondary" style={{ lineHeight: 1.5 }}>
									Choose a history type from the dropdown menu to display relevant form fields
								</Text>
							</div>
						</SectionCard>
					)}
				</Col>
			</Row>
		</div>
	);
};

export default CreateMedicalHistoryModal;
